import { Injectable, NgZone, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../auth/services/auth.service';
import { EventoAuditoria } from '../core/models/auditoria.model';
import { KvEntity } from '../core/models/asistencia.model';

export interface LoteOutboxMml {
  id: string;
  delta: Record<string, unknown[]>;
  nuevosEventosAuditoria: EventoAuditoria[];
  intentos: number;
  creadoEn: string;
  actualizadoEn: string;
  ultimoError?: string;
}

export interface EvidenciaPendienteMml {
  sesionId: string;
  nombreArchivo: string;
  tipoMime: string;
  tamanoBytes: number;
  blob: Blob;
  motivo?: string;
  creadoEn: string;
  intentos: number;
  ultimoError?: string;
}

export interface ResumenItemColaUi {
  id: string;
  tipo: 'DATOS' | 'FOTO_S3';
  sesionId?: string;
  descripcion: string;
  intentos: number;
  actualizadoEn: string;
  ultimoError?: string;
}

export type EstadoSincronizacionUi =
  | 'AL_DIA'
  | 'SINCRONIZANDO'
  | 'SIN_CONEXION'
  | 'REINTENTANDO_CON_ERROR';

const IDB_NAME = 'mj_asistencia_mml_idb';
const IDB_VERSION = 1;
const STORE_SNAPSHOT = 'snapshot';
const STORE_OUTBOX = 'outbox';
const STORE_EVIDENCIAS = 'evidencias_blobs';
const LOTE_ACTIVO_ID = 'lote-activo-mml';
const OUTBOX_STORAGE_KEY = 'mj_asistencia_mml_outbox_v1';

function claveEntidad(tabla: string, item: unknown): string {
  const obj = (item ?? {}) as Record<string, unknown>;
  if (tabla === 'preferencias') return `${obj['usuarioId']}|${obj['tipo']}`;
  if (tabla === 'plantillas') return `${obj['id']}|${obj['version']}`;
  if (tabla === 'kv') return String(obj['clave']);
  return String(obj['id'] ?? JSON.stringify(obj));
}

function fusionarFilasTabla(tabla: string, previas: unknown[], nuevas: unknown[]): unknown[] {
  const mapa = new Map<string, unknown>();
  for (const item of previas) {
    mapa.set(claveEntidad(tabla, item), item);
  }
  for (const item of nuevas) {
    mapa.set(claveEntidad(tabla, item), item);
  }
  return Array.from(mapa.values());
}

/**
 * Servicio de Cola Persistente (Outbox) + Almacenamiento Offline en IndexedDB para Asistencia MML.
 *
 * Garantías:
 * 1. Fusión inteligente (Coalescing): si el usuario guarda varias veces la misma clase o realiza
 *    varios cambios mientras no hay internet o hay un intento en curso, fusiona las entidades por
 *    su clave única en un solo lote consolidado (evita saturar el servidor con peticiones redundantes).
 * 2. Verificación estricta (ACK): ningún cambio ni foto se retira de la cola hasta que el servidor
 *    responde HTTP 200 con `{ ok: true, persistidoEnBd: true }` tras hacer COMMIT en PostgreSQL.
 * 3. Cero spam sin red + Backoff exponencial: si `navigator.onLine === false`, pausa por completo
 *    las peticiones de red. Si la red es intermitente, reintenta con backoff exponencial (4s -> 8s -> 16s -> 30s -> 60s).
 * 4. Fotos de evidencia en IndexedDB: si el mentor selecciona la foto con el grupo sin internet o
 *    falla la subida a S3, el Blob se conserva en IndexedDB y se sube automáticamente al reconectar.
 */
@Injectable({ providedIn: 'root' })
export class ColaSincronizacionMmlService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly ngZone = inject(NgZone);
  private readonly apiUrl = environment.URL_SERVER.endsWith('/')
    ? environment.URL_SERVER
    : `${environment.URL_SERVER}/`;

  private dbPromise: Promise<IDBDatabase | null> | null = null;
  private loteActual: LoteOutboxMml | null = this.cargarOutboxDesdeLocalStorage();
  private evidenciasMap = new Map<string, EvidenciaPendienteMml>();
  private urlsLocalesEvidencia = new Map<string, string>();

  private flushEnCurso = false;
  private necesitaNuevoFlush = false;
  private retryTimerId: ReturnType<typeof setTimeout> | null = null;
  private countdownTimerId: ReturnType<typeof setInterval> | null = null;

  private onEvidenciaConfirmadaCallback?: (info: {
    sesionId: string;
    evidenciaUrl: string;
    evidenciaNombre: string;
    tamanoBytes: number;
    motivo?: string;
  }) => Promise<void>;

  private onSincronizacionExitosaCallback?: (serverTimestamp?: string) => void;

  // --- Signals reactivos para la interfaz de usuario ---
  readonly enLinea = signal<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  readonly sincronizando = signal<boolean>(false);
  readonly ultimoError = signal<string | null>(null);
  readonly segundosParaReintento = signal<number>(0);
  readonly ultimaSincronizacionExitosaIso = signal<string | null>(null);
  readonly progresoFoto = signal<{
    sesionId: string;
    porcentaje: number;
    nombreArchivo: string;
    tamanoBytes: number;
    etapa: string;
  } | null>(null);

  private readonly versionCola = signal<number>(0);

  /** Conjunto de IDs de sesión que tienen asistencias o datos de sesión pendientes en la cola */
  readonly sesionesPendientesIds = computed<Set<string>>(() => {
    this.versionCola();
    const set = new Set<string>();
    const lote = this.loteActual;
    if (lote) {
      const sesiones = (lote.delta['sesiones'] ?? []) as Array<{ id?: string }>;
      for (const s of sesiones) {
        if (s?.id) set.add(s.id);
      }
      const asistencias = (lote.delta['asistencias'] ?? []) as Array<{ sesionId?: string }>;
      for (const a of asistencias) {
        if (a?.sesionId) set.add(a.sesionId);
      }
    }
    for (const sesionId of this.evidenciasMap.keys()) {
      set.add(sesionId);
    }
    return set;
  });

  /** Conjunto de IDs de sesión que tienen una foto de evidencia pendiente de subir a S3 */
  readonly fotosPendientesIds = computed<Set<string>>(() => {
    this.versionCola();
    return new Set(this.evidenciasMap.keys());
  });

  /** Detalle legible de los elementos actualmente en cola para mostrar al usuario */
  readonly itemsEnColaUi = computed<ResumenItemColaUi[]>(() => {
    this.versionCola();
    const items: ResumenItemColaUi[] = [];

    for (const ev of this.evidenciasMap.values()) {
      items.push({
        id: `foto-${ev.sesionId}`,
        tipo: 'FOTO_S3',
        sesionId: ev.sesionId,
        descripcion: `Foto con el grupo «${ev.nombreArchivo}» (${Math.max(1, Math.round(ev.tamanoBytes / 1024))} KB)`,
        intentos: ev.intentos,
        actualizadoEn: ev.creadoEn,
        ultimoError: ev.ultimoError
      });
    }

    const lote = this.loteActual;
    if (lote) {
      const partes: string[] = [];
      const nSes = (lote.delta['sesiones'] ?? []).length;
      const nAsis = (lote.delta['asistencias'] ?? []).length;
      const nGrupos = (lote.delta['grupos'] ?? []).length;
      const nModulos = (lote.delta['modulos'] ?? []).length;
      const nInsc = (lote.delta['inscripciones'] ?? []).length;
      const nPart = (lote.delta['participantes'] ?? []).length;

      if (nAsis > 0) partes.push(`${nAsis} registro(s) de asistencia`);
      if (nSes > 0) partes.push(`${nSes} clase(s)`);
      if (nGrupos > 0) partes.push(`${nGrupos} grupo(s)`);
      if (nModulos > 0) partes.push(`${nModulos} módulo(s)`);
      if (nInsc > 0) partes.push(`${nInsc} inscripción(es)`);
      if (nPart > 0) partes.push(`${nPart} participante(s)`);

      const ultimoEvento = lote.nuevosEventosAuditoria[lote.nuevosEventosAuditoria.length - 1];
      const descripcion = ultimoEvento?.resumen
        ? ultimoEvento.resumen
        : partes.length > 0
          ? `Cambios pendientes: ${partes.join(', ')}`
          : 'Actualización pendiente de confirmar en el servidor';

      items.push({
        id: lote.id,
        tipo: 'DATOS',
        sesionId: ultimoEvento?.contexto?.sesionId,
        descripcion,
        intentos: lote.intentos,
        actualizadoEn: lote.actualizadoEn,
        ultimoError: lote.ultimoError
      });
    }

    return items;
  });

  /** Cantidad total de paquetes pendientes por confirmar en el servidor */
  readonly pendientesCount = computed<number>(() => this.itemsEnColaUi().length);

  /** Estado general de sincronización para barras de aviso y badges */
  readonly estadoUi = computed<EstadoSincronizacionUi>(() => {
    const pendientes = this.pendientesCount();
    const online = this.enLinea();
    if (!online) return 'SIN_CONEXION';
    if (this.sincronizando()) return 'SINCRONIZANDO';
    if (pendientes > 0 && this.ultimoError()) return 'REINTENTANDO_CON_ERROR';
    if (pendientes > 0) return 'SINCRONIZANDO';
    return 'AL_DIA';
  });

  constructor() {
    void this.inicializarDesdeIndexedDb();

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.ngZone.run(() => {
          this.enLinea.set(true);
          this.ultimoError.set(null);
          this.limpiarTimersReintento();
          void this.flush();
        });
      });

      window.addEventListener('offline', () => {
        this.ngZone.run(() => {
          this.enLinea.set(false);
          this.limpiarTimersReintento();
        });
      });
    }
  }

  registrarCallbacksRepositorio(cbs: {
    onEvidenciaConfirmada: (info: {
      sesionId: string;
      evidenciaUrl: string;
      evidenciaNombre: string;
      tamanoBytes: number;
      motivo?: string;
    }) => Promise<void>;
    onSincronizacionExitosa?: (serverTimestamp?: string) => void;
  }): void {
    this.onEvidenciaConfirmadaCallback = cbs.onEvidenciaConfirmada;
    this.onSincronizacionExitosaCallback = cbs.onSincronizacionExitosa;
  }

  tienePendientes(): boolean {
    return Boolean(this.loteActual) || this.evidenciasMap.size > 0;
  }

  tienePendientesParaSesion(sesionId: string): boolean {
    if (!sesionId) return false;
    return this.sesionesPendientesIds().has(sesionId);
  }

  tieneFotoPendienteParaSesion(sesionId: string): boolean {
    if (!sesionId) return false;
    return this.evidenciasMap.has(sesionId);
  }

  obtenerUrlPreviaEvidenciaPendiente(sesionId: string): string | null {
    return this.urlsLocalesEvidencia.get(sesionId) ?? null;
  }

  obtenerMetaEvidenciaPendiente(sesionId: string): EvidenciaPendienteMml | undefined {
    this.versionCola();
    return this.evidenciasMap.get(sesionId);
  }

  /**
   * Obtiene las entidades pendientes en el outbox para fusionarlas sobre cualquier lectura del servidor
   * y evitar que un GET borre cambios que aún no han sido confirmados por el backend.
   */
  obtenerDeltaPendiente(): Record<string, unknown[]> | null {
    return this.loteActual ? this.loteActual.delta : null;
  }

  obtenerEventosPendientes(): EventoAuditoria[] {
    return this.loteActual ? this.loteActual.nuevosEventosAuditoria : [];
  }

  // ------------------------------------------------------------------------------------------
  // Almacenamiento de Snapshot Completo en IndexedDB (Soporte Offline de Clases del Día)
  // ------------------------------------------------------------------------------------------
  async guardarSnapshotIndexedDb(estado: unknown): Promise<void> {
    const db = await this.abrirDb();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_SNAPSHOT, 'readwrite');
        tx.objectStore(STORE_SNAPSHOT).put({
          id: 'estado_v3',
          actualizadoEn: new Date().toISOString(),
          datos: estado
        });
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  async cargarSnapshotIndexedDb<T>(): Promise<T | null> {
    const db = await this.abrirDb();
    if (!db) return null;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_SNAPSHOT, 'readonly');
        const req = tx.objectStore(STORE_SNAPSHOT).get('estado_v3');
        req.onsuccess = () => {
          const val = req.result as { datos?: T } | undefined;
          resolve(val?.datos ?? null);
        };
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }

  // ------------------------------------------------------------------------------------------
  // Encolado con Coalescencia (Fusión de Cambios)
  // ------------------------------------------------------------------------------------------
  encolarDelta(
    delta: Record<string, unknown>,
    nuevosEventosAuditoria: EventoAuditoria[]
  ): void {
    const deltaLimpio: Record<string, unknown[]> = {};
    for (const [k, v] of Object.entries(delta)) {
      if (!Array.isArray(v) || v.length === 0) continue;
      if (k === 'kv') {
        const kvFiltrado = (v as KvEntity[]).filter(item => item.clave !== 'lockProgramador');
        if (kvFiltrado.length > 0) deltaLimpio['kv'] = kvFiltrado;
      } else {
        deltaLimpio[k] = v;
      }
    }

    const hayCambios = Object.keys(deltaLimpio).length > 0 || nuevosEventosAuditoria.length > 0;
    if (!hayCambios) return;

    const ahoraIso = new Date().toISOString();
    if (!this.loteActual) {
      this.loteActual = {
        id: LOTE_ACTIVO_ID,
        delta: deltaLimpio,
        nuevosEventosAuditoria: [...nuevosEventosAuditoria],
        intentos: 0,
        creadoEn: ahoraIso,
        actualizadoEn: ahoraIso
      };
    } else {
      const deltaFusionado: Record<string, unknown[]> = { ...this.loteActual.delta };
      for (const [tabla, filasNuevas] of Object.entries(deltaLimpio)) {
        const previas = deltaFusionado[tabla] ?? [];
        deltaFusionado[tabla] = fusionarFilasTabla(tabla, previas, filasNuevas);
      }
      const eventosMap = new Map<string, EventoAuditoria>();
      for (const ev of this.loteActual.nuevosEventosAuditoria) {
        eventosMap.set(ev.id, ev);
      }
      for (const ev of nuevosEventosAuditoria) {
        eventosMap.set(ev.id, ev);
      }
      this.loteActual = {
        ...this.loteActual,
        delta: deltaFusionado,
        nuevosEventosAuditoria: Array.from(eventosMap.values()),
        actualizadoEn: ahoraIso
      };
    }

    this.persistirOutbox();
    this.notificarCambioCola();

    if (this.enLinea()) {
      void this.flush();
    }
  }

  // ------------------------------------------------------------------------------------------
  // Encolado y Subida Resiliente de Foto con el Grupo (S3 + IndexedDB)
  // ------------------------------------------------------------------------------------------
  async encolarFotoEvidencia(p: {
    sesionId: string;
    file: File;
    motivo?: string;
  }): Promise<{ estado: 'SUBIDA_OK' | 'ENCOLADA_OFFLINE'; url?: string; mensaje: string }> {
    const previaUrl = this.urlsLocalesEvidencia.get(p.sesionId);
    if (previaUrl) {
      URL.revokeObjectURL(previaUrl);
    }
    this.urlsLocalesEvidencia.set(p.sesionId, URL.createObjectURL(p.file));

    const item: EvidenciaPendienteMml = {
      sesionId: p.sesionId,
      nombreArchivo: p.file.name,
      tipoMime: p.file.type || 'image/jpeg',
      tamanoBytes: p.file.size,
      blob: p.file,
      motivo: p.motivo,
      creadoEn: new Date().toISOString(),
      intentos: 0
    };

    this.evidenciasMap.set(p.sesionId, item);
    await this.guardarEvidenciaEnIndexedDb(item);
    this.notificarCambioCola();

    if (!this.enLinea()) {
      return {
        estado: 'ENCOLADA_OFFLINE',
        mensaje: 'Sin conexión a internet: la foto con el grupo quedó guardada en este dispositivo y se subirá automáticamente apenas vuelva la señal.'
      };
    }

    await this.flush();

    if (!this.evidenciasMap.has(p.sesionId)) {
      return {
        estado: 'SUBIDA_OK',
        mensaje: 'Foto con el grupo subida y confirmada en el servidor.'
      };
    }

    return {
      estado: 'ENCOLADA_OFFLINE',
      mensaje: 'La señal está inestable: la foto quedó en cola segura en este dispositivo y se reintentará automáticamente hasta confirmar su subida.'
    };
  }

  // ------------------------------------------------------------------------------------------
  // Worker de Sincronización con Verificación Estricta (ACK) y Backoff Exponencial
  // ------------------------------------------------------------------------------------------
  async reintentarAhora(): Promise<void> {
    this.limpiarTimersReintento();
    this.ultimoError.set(null);
    await this.flush(true);
  }

  async flush(forzadoManual = false): Promise<boolean> {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.enLinea.set(false);
      return false;
    }
    this.enLinea.set(true);

    if (!this.tienePendientes()) {
      this.ultimoError.set(null);
      return true;
    }

    if (this.flushEnCurso) {
      this.necesitaNuevoFlush = true;
      return false;
    }

    this.flushEnCurso = true;
    this.sincronizando.set(true);
    this.limpiarTimersReintento();
    if (forzadoManual) {
      this.ultimoError.set(null);
    }

    let todoExitoso = true;

    try {
      // PASO 1: Subir fotos de evidencia pendientes a S3 (/upload-file)
      const fotosPendientes = Array.from(this.evidenciasMap.values());
      for (const foto of fotosPendientes) {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          this.enLinea.set(false);
          todoExitoso = false;
          break;
        }
        const okFoto = await this.subirUnaFotoEvidenciaS3(foto);
        if (!okFoto) {
          todoExitoso = false;
          break;
        }
      }

      // PASO 2: Enviar el lote consolidado de asistencias/sesiones/datos a PostgreSQL
      if (todoExitoso && this.loteActual) {
        const snapshotEnviado = structuredClone(this.loteActual);
        const timestampEnviado = snapshotEnviado.actualizadoEn;

        const exitoLote = await this.enviarLoteAlServidor(snapshotEnviado);
        if (exitoLote.ok) {
          // Verificar si durante el envío el usuario agregó más cambios nuevos al lote
          if (this.loteActual && this.loteActual.actualizadoEn === timestampEnviado) {
            this.loteActual = null;
          } else if (this.loteActual) {
            // Limpiamos únicamente los eventos de auditoría que ya fueron confirmados
            const idsConfirmados = new Set(snapshotEnviado.nuevosEventosAuditoria.map(e => e.id));
            this.loteActual = {
              ...this.loteActual,
              nuevosEventosAuditoria: this.loteActual.nuevosEventosAuditoria.filter(e => !idsConfirmados.has(e.id)),
              intentos: 0,
              ultimoError: undefined
            };
            const quedanCambios =
              Object.values(this.loteActual.delta).some(arr => Array.isArray(arr) && arr.length > 0) ||
              this.loteActual.nuevosEventosAuditoria.length > 0;
            if (!quedanCambios) {
              this.loteActual = null;
            }
          }
          this.persistirOutbox();
          this.ultimoError.set(null);
          const ahoraConfirmado = exitoLote.serverTimestamp || new Date().toISOString();
          this.ultimaSincronizacionExitosaIso.set(ahoraConfirmado);
          this.onSincronizacionExitosaCallback?.(ahoraConfirmado);
          this.notificarCambioCola();
        } else {
          todoExitoso = false;
          const intentos = (this.loteActual?.intentos ?? 0) + 1;
          const msgError = exitoLote.mensajeError || 'No se pudo confirmar el guardado en el servidor. Se reintentará automáticamente.';
          if (this.loteActual) {
            this.loteActual = {
              ...this.loteActual,
              intentos,
              ultimoError: msgError
            };
            this.persistirOutbox();
          }
          this.ultimoError.set(msgError);
          this.notificarCambioCola();
          this.programarReintentoBackoff(intentos);
        }
      }
    } finally {
      this.flushEnCurso = false;
      this.sincronizando.set(false);
    }

    if (this.necesitaNuevoFlush && todoExitoso && this.enLinea()) {
      this.necesitaNuevoFlush = false;
      return this.flush();
    }
    this.necesitaNuevoFlush = false;
    return todoExitoso;
  }

  private async subirUnaFotoEvidenciaS3(foto: EvidenciaPendienteMml): Promise<boolean> {
    const ext = (foto.nombreArchivo.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const nombreUnico = `evidencia-${foto.sesionId}-${Date.now()}.${ext}`;

    const formData = new FormData();
    formData.append('bucket', 'medellinjoven-production');
    formData.append('folder', 'asistencia-mml/evidencias');
    formData.append('file', foto.blob, nombreUnico);

    this.progresoFoto.set({
      sesionId: foto.sesionId,
      porcentaje: 8,
      nombreArchivo: foto.nombreArchivo,
      tamanoBytes: foto.tamanoBytes,
      etapa: 'Subiendo foto con el grupo a la nube…'
    });

    try {
      const req = new HttpRequest('POST', `${this.apiUrl}upload-file`, formData, {
        reportProgress: true
      });

      const urlSubida = await new Promise<string>((resolve, reject) => {
        this.http
          .request<{ success: boolean; url: string; message?: string }>(req)
          .pipe(timeout(45_000))
          .subscribe({
            next: (event) => {
              if (event.type === HttpEventType.UploadProgress) {
                const total = event.total || foto.tamanoBytes || 1;
                const pct = Math.min(90, Math.max(10, Math.round((event.loaded / total) * 90)));
                this.progresoFoto.set({
                  sesionId: foto.sesionId,
                  porcentaje: pct,
                  nombreArchivo: foto.nombreArchivo,
                  tamanoBytes: foto.tamanoBytes,
                  etapa: 'Subiendo foto con el grupo a la nube…'
                });
              } else if (event.type === HttpEventType.Response) {
                const body = event.body;
                if (body?.success && body.url) {
                  resolve(body.url);
                } else {
                  reject(new Error(body?.message || 'El servidor de archivos no confirmó la recepción de la imagen.'));
                }
              }
            },
            error: (err) => {
              reject(err);
            }
          });
      });

      this.progresoFoto.set({
        sesionId: foto.sesionId,
        porcentaje: 95,
        nombreArchivo: foto.nombreArchivo,
        tamanoBytes: foto.tamanoBytes,
        etapa: 'Asociando foto a la clase en la base de datos…'
      });

      const baseS3 = environment.URL_S3.endsWith('/') ? environment.URL_S3 : `${environment.URL_S3}/`;
      const urlCompleta = urlSubida.startsWith('http')
        ? urlSubida
        : `${baseS3}${urlSubida.replace(/^\//, '')}`;

      if (this.onEvidenciaConfirmadaCallback) {
        await this.onEvidenciaConfirmadaCallback({
          sesionId: foto.sesionId,
          evidenciaUrl: urlCompleta,
          evidenciaNombre: foto.nombreArchivo,
          tamanoBytes: foto.tamanoBytes,
          motivo: foto.motivo
        });
      }

      // Solo tras haber obtenido la URL de S3 y registrado en el estado local/outbox, retiramos el Blob pendiente
      this.evidenciasMap.delete(foto.sesionId);
      const urlLocal = this.urlsLocalesEvidencia.get(foto.sesionId);
      if (urlLocal) {
        URL.revokeObjectURL(urlLocal);
        this.urlsLocalesEvidencia.delete(foto.sesionId);
      }
      await this.eliminarEvidenciaDeIndexedDb(foto.sesionId);
      this.progresoFoto.set(null);
      this.notificarCambioCola();
      return true;
    } catch (err: any) {
      this.progresoFoto.set(null);
      const intentos = (foto.intentos ?? 0) + 1;
      const esTimeout = err?.name === 'TimeoutError';
      const msg = esTimeout
        ? 'Señal muy débil: la subida de la foto tardó demasiado. La imagen sigue guardada en este dispositivo y se reintentará automáticamente.'
        : (err?.error?.message || err?.message || 'No se pudo subir la foto por inestabilidad de red. Se reintentará automáticamente.');
      const actualizada: EvidenciaPendienteMml = {
        ...foto,
        intentos,
        ultimoError: msg
      };
      this.evidenciasMap.set(foto.sesionId, actualizada);
      await this.guardarEvidenciaEnIndexedDb(actualizada);
      this.ultimoError.set(msg);
      this.notificarCambioCola();
      this.programarReintentoBackoff(intentos);
      return false;
    }
  }

  private async enviarLoteAlServidor(lote: LoteOutboxMml): Promise<{
    ok: boolean;
    serverTimestamp?: string;
    mensajeError?: string;
  }> {
    const rutaActual = typeof window !== 'undefined' ? window.location.pathname : '';
    const esRutaPublicaOEstudiante =
      rutaActual.startsWith('/inscripcion-mml') || rutaActual.startsWith('/mi-progreso-mml');
    const urlPublica = `${this.apiUrl}mml-asistencia/sincronizar-inscripcion`;
    const urlAdmin = `${this.apiUrl}mml-asistencia/sincronizar`;
    const usarAdmin = Boolean(this.authService.getToken()) && !esRutaPublicaOEstudiante;

    const body = {
      ...lote.delta,
      nuevosEventosAuditoria: lote.nuevosEventosAuditoria
    };

    try {
      const resp = await firstValueFrom(
        this.http
          .post<{
            ok?: boolean;
            persistidoEnBd?: boolean;
            serverTimestamp?: string;
            message?: string;
          }>(usarAdmin ? urlAdmin : urlPublica, body)
          .pipe(timeout(15_000))
      );

      if (resp && resp.ok === true && resp.persistidoEnBd !== false) {
        return { ok: true, serverTimestamp: resp.serverTimestamp };
      }
      return {
        ok: false,
        mensajeError: resp?.message || 'El servidor recibió la solicitud pero no confirmó el guardado en la base de datos.'
      };
    } catch (err: any) {
      const st = Number(err?.status ?? 0);
      const esTimeout = err?.name === 'TimeoutError';
      // Fallback al endpoint de sincronización si el token expiró temporalmente en segundo plano
      if (usarAdmin && (st === 401 || st === 403 || st === 419 || st === 420)) {
        try {
          const respFallback = await firstValueFrom(
            this.http
              .post<{
                ok?: boolean;
                persistidoEnBd?: boolean;
                serverTimestamp?: string;
                message?: string;
              }>(urlPublica, body)
              .pipe(timeout(15_000))
          );
          if (respFallback && respFallback.ok === true && respFallback.persistidoEnBd !== false) {
            return { ok: true, serverTimestamp: respFallback.serverTimestamp };
          }
        } catch {
          // Continúa al manejo estándar de error abajo
        }
      }

      if (esTimeout) {
        return {
          ok: false,
          mensajeError: 'Tu señal de internet está muy débil y la petición se cortó por tiempo de espera. Tus cambios siguen guardados en la cola y se reintentarán automáticamente.'
        };
      }

      if (st === 0 || (typeof navigator !== 'undefined' && !navigator.onLine)) {
        return {
          ok: false,
          mensajeError: 'Sin conexión estable con el servidor. Tus cambios siguen guardados en este dispositivo y se subirán automáticamente.'
        };
      }
      return {
        ok: false,
        mensajeError: err?.error?.message || `El servidor tardó en responder o devolvió un error (${st || 'red'}). Tus cambios siguen seguros en la cola.`
      };
    }
  }

  private programarReintentoBackoff(intentos: number): void {
    this.limpiarTimersReintento();
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    // Backoff exponencial controlado: 4s, 8s, 16s, 30s, máx 60s (nunca sobrecarga el servidor)
    const baseSeg = Math.min(60, Math.round(4 * Math.pow(1.8, Math.max(0, intentos - 1))));
    this.segundosParaReintento.set(baseSeg);

    this.ngZone.runOutsideAngular(() => {
      this.countdownTimerId = setInterval(() => {
        const rest = this.segundosParaReintento() - 1;
        if (rest >= 0) {
          this.ngZone.run(() => this.segundosParaReintento.set(rest));
        }
      }, 1000);

      this.retryTimerId = setTimeout(() => {
        this.ngZone.run(() => {
          this.limpiarTimersReintento();
          if (this.enLinea() && this.tienePendientes()) {
            void this.flush();
          }
        });
      }, baseSeg * 1000);
    });
  }

  private limpiarTimersReintento(): void {
    if (this.retryTimerId) {
      clearTimeout(this.retryTimerId);
      this.retryTimerId = null;
    }
    if (this.countdownTimerId) {
      clearInterval(this.countdownTimerId);
      this.countdownTimerId = null;
    }
    this.segundosParaReintento.set(0);
  }

  private notificarCambioCola(): void {
    this.versionCola.update(v => v + 1);
  }

  // ------------------------------------------------------------------------------------------
  // Persistencia de Outbox y Blobs en localStorage + IndexedDB
  // ------------------------------------------------------------------------------------------
  private cargarOutboxDesdeLocalStorage(): LoteOutboxMml | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(OUTBOX_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as LoteOutboxMml;
      if (parsed && parsed.delta && typeof parsed.delta === 'object') {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  }

  private persistirOutbox(): void {
    if (typeof localStorage !== 'undefined') {
      try {
        if (this.loteActual) {
          localStorage.setItem(OUTBOX_STORAGE_KEY, JSON.stringify(this.loteActual));
        } else {
          localStorage.removeItem(OUTBOX_STORAGE_KEY);
        }
      } catch {
        // Ignorar si localStorage supera cuota; IndexedDB guarda el respaldo completo
      }
    }
    void this. sincronizarOutboxEnIndexedDb();
  }

  private async abrirDb(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') return null;
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(IDB_NAME, IDB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_SNAPSHOT)) {
            db.createObjectStore(STORE_SNAPSHOT, { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
            db.createObjectStore(STORE_OUTBOX, { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains(STORE_EVIDENCIAS)) {
            db.createObjectStore(STORE_EVIDENCIAS, { keyPath: 'sesionId' });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });

    return this.dbPromise;
  }

  private async inicializarDesdeIndexedDb(): Promise<void> {
    const db = await this.abrirDb();
    if (!db) return;

    // 1. Recuperar lote outbox si no estaba en localStorage
    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE_OUTBOX, 'readonly');
        const req = tx.objectStore(STORE_OUTBOX).get(LOTE_ACTIVO_ID);
        req.onsuccess = () => {
          const guardado = req.result as LoteOutboxMml | undefined;
          if (guardado && !this.loteActual) {
            this.loteActual = guardado;
          }
          resolve();
        };
        req.onerror = () => resolve();
      } catch {
        resolve();
      }
    });

    // 2. Recuperar fotos pendientes de S3
    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE_EVIDENCIAS, 'readonly');
        const req = tx.objectStore(STORE_EVIDENCIAS).getAll();
        req.onsuccess = () => {
          const lista = (req.result ?? []) as EvidenciaPendienteMml[];
          for (const item of lista) {
            if (item?.sesionId && item.blob) {
              this.evidenciasMap.set(item.sesionId, item);
              if (!this.urlsLocalesEvidencia.has(item.sesionId)) {
                this.urlsLocalesEvidencia.set(item.sesionId, URL.createObjectURL(item.blob));
              }
            }
          }
          resolve();
        };
        req.onerror = () => resolve();
      } catch {
        resolve();
      }
    });

    this.notificarCambioCola();
    if (this.enLinea() && this.tienePendientes()) {
      void this.flush();
    }
  }

  private async sincronizarOutboxEnIndexedDb(): Promise<void> {
    const db = await this.abrirDb();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_OUTBOX, 'readwrite');
        const store = tx.objectStore(STORE_OUTBOX);
        if (this.loteActual) {
          store.put(this.loteActual);
        } else {
          store.delete(LOTE_ACTIVO_ID);
        }
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  private async guardarEvidenciaEnIndexedDb(item: EvidenciaPendienteMml): Promise<void> {
    const db = await this.abrirDb();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_EVIDENCIAS, 'readwrite');
        tx.objectStore(STORE_EVIDENCIAS).put(item);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  private async eliminarEvidenciaDeIndexedDb(sesionId: string): Promise<void> {
    const db = await this.abrirDb();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_EVIDENCIAS, 'readwrite');
        tx.objectStore(STORE_EVIDENCIAS).delete(sesionId);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }
}
